import React from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { grey, lightBlue, yellow } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import moment from "moment";
import { displayDateFormat } from "utils/const";

const StyledAvatar = styled(Avatar)(({ theme: { palette } }) => ({
  border: `1px solid ${palette.grey[400]}`,
  backgroundColor: palette.grey[200],
  color: palette.getContrastText(palette.grey[200]),
}));

export enum MessagePosition {
  left = "left",
  right = "right",
}

export enum MessageColor {
  grey = "grey",
  blue = "blue",
  yellow = "yellow",
}

type MessageProps = {
  message: string;
  createdAt: Date;
  position: MessagePosition;
  displayName: string;
  color?: MessageColor;
  iconText?: string;
};

const colorList: Record<
  MessageColor,
  {
    backgroundColor: string;
    borderColor: string;
    beforeBorderTopColor: string;
    afterBorderTopColor: string;
  }
> = {
  [MessageColor.yellow]: {
    backgroundColor: yellow[100],
    borderColor: yellow[600],
    beforeBorderTopColor: yellow[600],
    afterBorderTopColor: yellow[100],
  },
  [MessageColor.blue]: {
    backgroundColor: lightBlue[50],
    borderColor: lightBlue[400],
    beforeBorderTopColor: lightBlue[400],
    afterBorderTopColor: lightBlue[50],
  },
  [MessageColor.grey]: {
    backgroundColor: grey[200],
    borderColor: grey[400],
    beforeBorderTopColor: grey[400],
    afterBorderTopColor: grey[200],
  },
};

export const Message: React.FC<MessageProps> = (props) => {
  const { color = MessageColor.grey } = props;

  const message = props.message ?? "";
  const timestamp = props.createdAt
    ? moment(props.createdAt).format(displayDateFormat)
    : "unknown time";
  const displayName = props.displayName ? props.displayName : "-";

  const isLeftMessage = props.position === MessagePosition.left;

  return (
    <Box display="flex" justifyContent={isLeftMessage ? "flex-start" : "flex-end"}>
      {isLeftMessage && <StyledAvatar alt={displayName}>{props.iconText ?? null}</StyledAvatar>}

      <Box>
        <Box
          width="fit-content"
          maxWidth="90%"
          position="relative"
          margin="15px"
          marginLeft={{ xs: "15px", sm: "20px" }}
          padding="10px"
          border="1px solid"
          borderColor={colorList[color].borderColor}
          borderRadius="10px"
          bgcolor={colorList[color].backgroundColor}
          fontSize="0.9em"
          fontWeight="400"
          fontFamily="'Open Sans', sans-serif"
          textAlign="left"
          sx={{
            "&:before": {
              content: "''",
              width: "0",
              height: "0",
              position: "absolute",
              top: "-1px",
              borderTop: "12px solid",
              borderTopColor: colorList[color].beforeBorderTopColor,
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",

              ...(isLeftMessage
                ? {
                    left: "-17px",
                    marginLeft: "6px",
                  }
                : {
                    right: "-17px",
                    marginRight: "6px",
                  }),
            },
            "&:after": {
              content: "''",
              width: "0",
              height: "0",
              position: "absolute",
              top: "0",
              borderTop: "13px solid",
              borderTopColor: colorList[color].afterBorderTopColor,
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",

              ...(isLeftMessage
                ? {
                    left: "-15px",
                    marginLeft: "6px",
                  }
                : {
                    right: "-15px",
                    marginRight: "6px",
                  }),
            },
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            {displayName}
          </Typography>

          <Typography margin={0} padding={0} fontSize={13}>
            {message}
          </Typography>

          <Typography marginTop="10px" fontSize="0.6em" fontWeight={300} sx={{ float: "right" }}>
            {timestamp}
          </Typography>
        </Box>
      </Box>

      {!isLeftMessage && <StyledAvatar alt={displayName}>{props.iconText ?? null}</StyledAvatar>}
    </Box>
  );
};
